import type { ReplacePropType } from 'utils/types';
import type { Nullable } from 'utils/values';

import type { AccessTokenStore } from './useAccessTokenStore';

type AccessTokenPayload = Pick<
  NonNullable<AccessTokenStore['accessTokenPayload']>,
  | 'sub'
  | 'role'
  | 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
  | 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'
  | 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'
>;

type PickedState = ReplacePropType<
  Pick<AccessTokenStore, 'accessTokenPayload'>,
  'accessTokenPayload',
  Nullable<AccessTokenPayload>
>;

export function getSubFromClaim<S extends PickedState>(state: S) {
  const rawSub = state.accessTokenPayload?.sub || '';
  return rawSub.replace('-', '');
}

/** Retrieve email from the access token payload.*/
export function getEmailFromClaim<S extends PickedState>(state: S) {
  return state.accessTokenPayload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '';
}

export function getIsInternalUser<S extends PickedState>(state: S) {
  const email = getEmailFromClaim(state);
  return email.endsWith('@levvr.io') || email.endsWith('@semantx.nl');
}

/** Retrieve given name from the access token payload.*/
export function getGivenNameFromClaim<S extends PickedState>(state: S) {
  return state.accessTokenPayload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] || '';
}

/** Retrieve surname from the access token payload.*/
export function getSurnameFromClaim<S extends PickedState>(state: S) {
  return state.accessTokenPayload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] || '';
}

export function getUserInfoFromClaim<S extends PickedState>(state: S) {
  const sub = getSubFromClaim(state);
  const firstName = getGivenNameFromClaim(state);
  const lastName = getSurnameFromClaim(state);
  const emailAddress = getEmailFromClaim(state);

  const fullName = `${firstName} ${lastName}`.trim() || emailAddress;
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || emailAddress.charAt(0).toUpperCase();

  return {
    sub,
    firstName,
    lastName,

    fullName,

    username: emailAddress,
    initials,

    tooltip: `${firstName} ${lastName} (${emailAddress})`
  };
}

export function getIsOwnerFromClaim<S extends PickedState>(state: S) {
  const role = state.accessTokenPayload?.role;

  if (!role) {
    return false;
  }

  return role.includes('owner');
}
